import requestWrapper from '../../requestWrapper';
import endpoints from './endpoints';
import type {
	Pitch,
	PitchCreateRequest,
	PitchCreateResponse,
	PitchDeleteResponse,
	PitchImportResponse,
	PitchSearchParams,
	PitchUpdateRequest,
	PitchUpdateResponse,
} from '@deezer/next/services/api/Starmania/requests/pitches/types';
import type {DataResponseWrapper} from '@deezer/next/services/api/Starmania/requests/types';
import type {PitchActiveFilterType} from '@deezer/next/features/Pitches/types';
import {notNull} from '@deezer/next/shared/utils/array';
import {getPeriodDates} from '@components/DateRangePicker/Pickers/PeriodSelector';
import {TimeframePickerFrequency} from '@components/Filters/types';
import type {PeriodType} from '@components/DateRangePicker/types';
import type {ReleaseNameSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';

const {post, patch, get, paginatedGet} = requestWrapper;

const requests = {
	createPitch: (data: PitchCreateRequest, id?: number) => {
		if (id) {
			const dataWithId = {...data, providerId: id};
			return post<DataResponseWrapper<PitchCreateResponse>>(
				endpoints.pitches,
				dataWithId,
			);
		} else {
			return post<DataResponseWrapper<PitchCreateResponse>>(
				endpoints.pitches,
				data,
			);
		}
	},
	editPitch: (id: number, data: PitchUpdateRequest) => {
		return patch<DataResponseWrapper<PitchUpdateResponse>>(
			endpoints.pitch(id),
			data,
		);
	},
	deletePitch: (id: number) => {
		return requestWrapper.delete<DataResponseWrapper<PitchDeleteResponse>>(
			endpoints.pitch(id),
		);
	},
	getPitches: (filters?: PitchActiveFilterType, q?: string) => {
		const providerNames = filters?.providers?.map(p => p.name)?.filter(notNull);
		const labelNames = filters?.labels?.map(l => l.name)?.filter(notNull);
		const creationPeriodDates =
			filters && getPeriodDates(filters.creationPeriodDate as PeriodType);
		const albumReleasePeriodDates =
			filters && getPeriodDates(filters.albumReleasePeriodDate as PeriodType);
		return paginatedGet<Pitch[]>(endpoints.pitches, {
			params: {
				q: q || undefined,
				onlyPreferredPartners: filters?.onlyPreferredPartners || false,
				providerName: providerNames,
				labelName: labelNames,
				editoPriority: filters?.editoPriorities,
				genres: filters?.genres,
				priority: filters?.priorities,
				status: filters?.status || undefined,
				artistName: filters?.artistName || undefined,
				albumName:
					filters?.albumName === 'All'
						? undefined
						: filters?.albumName || undefined,
				editoTeams: filters?.editoTeams,
				sort: filters?.sort || undefined,
				sortOrder: filters?.sortOrder || undefined,
				startCreationDate:
					filters?.creationDateFrequency === TimeframePickerFrequency.PERIOD &&
					filters.creationPeriodDate
						? creationPeriodDates?.startDate.toISOString().split('T')[0]
						: filters?.startCreationDate || undefined,
				endCreationDate:
					filters?.creationDateFrequency === TimeframePickerFrequency.PERIOD &&
					filters.creationPeriodDate
						? creationPeriodDates?.endDate.toISOString().split('T')[0]
						: filters?.endCreationDate || undefined,
				startAlbumReleaseDate:
					filters?.albumReleaseDateFrequency ===
						TimeframePickerFrequency.PERIOD && filters.albumReleasePeriodDate
						? albumReleasePeriodDates?.startDate.toISOString().split('T')[0]
						: filters?.startAlbumReleaseDate || undefined,
				endAlbumReleaseDate:
					filters?.albumReleaseDateFrequency ===
						TimeframePickerFrequency.PERIOD && filters.albumReleasePeriodDate
						? albumReleasePeriodDates?.endDate.toISOString().split('T')[0]
						: filters?.endAlbumReleaseDate || undefined,
				upc: filters?.upcs,
			},
		});
	},
	searchPitches: (queryString: string, params?: PitchSearchParams) => {
		return paginatedGet<Pitch[]>(endpoints.pitches, {
			params: {
				q: queryString,
				sort: params?.sort || undefined,
				sortOrder: params?.sortOrder || undefined,
			},
		});
	},
	getPitch: (id: number) => {
		return get<DataResponseWrapper<Pitch>>(endpoints.pitch(id));
	},
	importPitches: (pitches: File) => {
		const body = new FormData();
		body.append('action', 'POST');
		body.append('pitches', pitches);
		return post<
			DataResponseWrapper<{importedLines: number; total: number}> & {
				errors: PitchImportResponse[];
			}
		>(endpoints.importPitches, body);
	},
	getPitchReleases: (releaseName: string) => {
		return get<DataResponseWrapper<ReleaseNameSearchResult[]>>(
			endpoints.pitchReleases,
			{
				params: {releaseName},
			},
		);
	},
};

export default requests;
